import React from "react";
import { useVaultGlobals } from "../hooks/useVault";
import { fmtBnb, fmtToken, CloudPattern } from "./Brand";
import { VAULT_ADDRESS, TOKEN_ADDRESS } from "../config/contracts";

function Stat({ label, value, sub }) {
  return (
    <div className="glass-dark px-5 py-4">
      <div className="text-xs tracking-widest text-emerald-200/60">{label}</div>
      <div className="font-num text-2xl tabular-nums mt-1 gold-shimmer">{value}</div>
      {sub && <div className="text-[11px] text-emerald-100/50 mt-1">{sub}</div>}
    </div>
  );
}

export default function About() {
  const g = useVaultGlobals();

  return (
    <section id="about" className="relative z-10 mt-14 px-6 md:px-10">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="font-display text-2xl md:text-3xl font-bold gold-shimmer">🪶 链上透明</h2>
          <p className="text-emerald-100/60 text-sm mt-1">所有规则与资金流向，BscScan 可查</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 glass gold-border p-6 md:p-8 relative overflow-hidden">
          <CloudPattern className="absolute -top-2 left-0 right-0 w-full opacity-50" />
          <h3 className="font-display text-xl text-amber-300">奖池数据 · 链上实时</h3>
          <p className="text-sm text-emerald-100/70 mt-2 leading-relaxed">
            每一笔代币买卖触发的销售税自动注入金库，转化为奖池 BNB；中奖时由 VRF 回调自动发放至中奖者钱包。
            下面两项是链上累计快照（每 10 秒刷新）：
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="glass-dark px-6 py-5">
              <div className="text-xs tracking-widest text-emerald-200/60">📥 累计税费收入</div>
              <div className="font-num text-4xl tabular-nums mt-2 gold-shimmer">
                {fmtBnb(g.stats?.totalTaxReceived, 4)}
              </div>
              <div className="text-xs text-emerald-100/50 mt-1">BNB · 历史累计税费总额</div>
            </div>
            <div className="glass-dark px-6 py-5">
              <div className="text-xs tracking-widest text-emerald-200/60">🏆 累计已发奖金</div>
              <div className="font-num text-4xl tabular-nums mt-2 gold-shimmer">
                {fmtBnb(g.stats?.totalPrizesPaid, 4)}
              </div>
              <div className="text-xs text-emerald-100/50 mt-1">BNB · 已通过 VRF 回调发放给中奖者</div>
            </div>
          </div>

          <div className="mt-6 flex justify-center">
            <img
              src="https://customer-assets.emergentagent.com/job_dragon-boat-draw/artifacts/oit5tsuo_%E6%8A%A0%E5%9B%BE%20%285%29.png"
              alt="LuckyZongzi Dragon Boat"
              className="w-full max-w-md float-y"
              style={{ filter: "drop-shadow(0 20px 40px rgba(243,186,47,0.25))" }}
              data-testid="hero-dragon-boat"
            />
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="glass gold-border p-6">
            <h3 className="font-display text-xl text-amber-300">合约地址</h3>
            <div className="mt-4 space-y-3 text-sm">
              <div>
                <div className="text-xs text-emerald-200/60">本金库 (Vault)</div>
                {VAULT_ADDRESS ? (
                  <a className="addr text-emerald-200 underline break-all hover:text-amber-300"
                     href={`https://bscscan.com/address/${VAULT_ADDRESS}`} target="_blank" rel="noreferrer" data-testid="link-vault">
                    {VAULT_ADDRESS}
                  </a>
                ) : (
                  <span className="text-amber-300/90 text-xs">⏳ 活动未开始，敬请期待</span>
                )}
              </div>
              <div>
                <div className="text-xs text-emerald-200/60">代币合约 (Token)</div>
                {TOKEN_ADDRESS ? (
                  <a className="addr text-emerald-200 underline break-all hover:text-amber-300"
                     href={`https://bscscan.com/address/${TOKEN_ADDRESS}`} target="_blank" rel="noreferrer" data-testid="link-token">
                    {TOKEN_ADDRESS}
                  </a>
                ) : (
                  <span className="text-amber-300/90 text-xs">⏳ 活动未开始，敬请期待</span>
                )}
              </div>
              <div>
                <div className="text-xs text-emerald-200/60">随机数源</div>
                <a className="text-emerald-200 underline hover:text-amber-300"
                   href="https://docs.chain.link/vrf/v2-5/supported-networks#bnb-chain"
                   target="_blank" rel="noreferrer" data-testid="link-vrf">
                  Chainlink VRF v2.5 · BNB Chain ↗
                </a>
              </div>
            </div>
          </div>

          <div className="glass gold-border p-6">
            <h3 className="font-display text-xl text-amber-300">参与规则</h3>
            <ol className="mt-4 space-y-2 text-sm text-emerald-100/85 list-decimal pl-5">
              <li>钱包持有 ≥ <span className="font-num text-amber-300">{fmtToken(g.holdingThreshold)}</span> 枚幸运粽子代币</li>
              <li>签到记录当前持仓快照</li>
              <li>等待 <span className="font-num text-amber-300">{Number(g.minHoldTime || 900n) / 60}</span> 分钟（防女巫）</li>
              <li>抽奖一次（每地址 <span className="font-num text-amber-300">{Math.floor(Number(g.cooldown || 3600n) / 60)}</span> 分钟一次）</li>
              <li>中奖 BNB 由 Chainlink VRF 回调时自动到账</li>
            </ol>
            <p className="text-[11px] text-emerald-100/50 mt-3">
              抽奖完全免费，奖金来自代币交易税。若奖池余额 &lt; 0.3 BNB 自动暂停，回升后自动恢复。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
